// ./src/utils/api/DiscountCodeService.ts
//
// Checkout discount codes (percent and/or flat off), scoped to an event. Mirrors
// the eventsmagic-api DiscountCode SDL. `where.id` accepts one id or many — a bulk
// update (>1 id) may only change status (server-enforced). The code is stored
// uppercased for case-insensitive matching.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FIELDS = `
  id
  eventId
  code
  name
  description
  percentOff
  amountOff
  currencyCode
  minOrderAmount
  maxUses
  maxUsesPerCustomer
  usedCount
  startsAt
  endsAt
  appliesTo
  ticketTypeIds
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query DiscountCodeList($filter: DiscountCodeFilter, $params: PaginationAndSorting) {
    discountCodeList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation DiscountCodeCreate($params: DiscountCodeInput) {
    discountCodeCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation DiscountCodeUpdate($where: DiscountCodeWhereInput, $params: DiscountCodeInput) {
    discountCodeUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation DiscountCodeRemove($where: DiscountCodeWhereInput) {
    discountCodeRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface DiscountCode {
  id?: string;
  eventId?: string | null;
  code?: string | null;
  name?: string | null;
  description?: string | null;
  percentOff?: number | null;
  amountOff?: number | null;
  currencyCode?: string | null;
  minOrderAmount?: number | null;
  maxUses?: number | null;
  maxUsesPerCustomer?: number | null;
  usedCount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  appliesTo?: string | null;
  /** #163 — with appliesTo 'ticket_types', the event's ticket types the code reduces. */
  ticketTypeIds?: string[] | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DiscountCodeInput {
  eventId?: string | null;
  code?: string | null;
  name?: string | null;
  description?: string | null;
  percentOff?: number | null;
  amountOff?: number | null;
  currencyCode?: string | null;
  minOrderAmount?: number | null;
  maxUses?: number | null;
  maxUsesPerCustomer?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  appliesTo?: string | null;
  ticketTypeIds?: string[] | null;
  status?: number | null;
}

export class DiscountCodeService {
  static async list(eventId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], status: statuses },
    });
  }

  static async create(params: DiscountCodeInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: DiscountCodeInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
